import React, { useEffect, useState, useRef } from "react";
import { sortByName, getColor, humanizeDate, unique } from "../utils";
import api from "../api";

export const PortionsContext = React.createContext({});

export const PortionsProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [portions, setPortions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [developmentsSearched, setDevelopmentsSearched] = useState([]);
  const [districtsSearched, setDistrictsSearched] = useState([]);
  const [currentSearch, setCurrentSearch] = useState("");
  const [selectedLoteamiento, setSelectedLoteamiento] = useState(null);
  const [blocksCentralPoints, setBlocksCentralPoints] = useState([]);
  const [lastUpdate, setLastUpdate] = useState();
  const [showPortionsList, setShowPortionsList] = useState(false);
  const [parcels, setParcels] = useState(
    {
      type: "FeatureCollection",
      features: []
    }
  );
  const [viewport, setViewport] = useState({
    width: "calc(100% - var(--filters-width))",
    height: "calc(100vh - var(--header-height)",
    latitude: -26.43942302360755,
    longitude: -56.46367554480092,
    zoom: 7, // if you change this value, CHANGE the conditional value in changeViewport function
    // look for 'this.state.viewport.zoom !== current value' and change it to 'this.state.viewport.zoom !== new value', map.js line 138 aprox
    bearing: 0,
    pitch: 0,
  });
  const [initData, setInitData] = useState({});
  const [searching, setSearching] = useState(false);
  const map = useRef();

  const fetchData = async () => {
    const portionsResponse = await api.get("v1/development-groups/");
    setPortions(sortByName(portionsResponse.data.features))
    setDevelopments(sortByName(portionsResponse.data.features))
    setDevelopmentsSearched(sortByName(portionsResponse.data.features))

    const districtsResponse = await api.get('v1/districts/');
    setDistricts(sortByName(districtsResponse.data))
    setDistrictsSearched(sortByName(districtsResponse.data))

    const departmentsResponse = sortByName(districtsResponse.data.map(district => district.department));
    setDepartments(unique(departmentsResponse));

    const settingsResponse = await api.get("v1/settings");
    setLastUpdate(humanizeDate(settingsResponse.data.last_sync));

    map.current = {
      markers: portions
    };

    setInitData({
      portions: sortByName(portionsResponse.data.features),
      developments: sortByName(portionsResponse.data.features),
      developmentsSearched: sortByName(portionsResponse.data.features),
      districts: sortByName(districtsResponse.data),
      districtsSearched: sortByName(districtsResponse.data),
      departments: unique(sortByName(departmentsResponse)),
    })

    setIsLoading(false);
  }

  const search = async (query) => {
    if (query.length > 0) {
      const result = await api.get(`v1/search/?q=${query}`);
      console.log(result.data);
      setDistrictsSearched(sortByName(result.data.districts));
      setDevelopmentsSearched(sortByName(result?.data?.developments?.features || []));
      setCurrentSearch(query);
      setShowPortionsList(true);
    } else {
      clearSearch();
    }
  }

  const clearSearch = () => {
    setDistrictsSearched(districts);
    setDevelopmentsSearched(developments);
    setCurrentSearch("");
    setShowPortionsList(false);
  }


  const setCurrentLoteamiento = (development) => {
    if (
      !selectedLoteamiento ||
      selectedLoteamiento.id !== development.id
    ) {
      // Realiza una solicitud para obtener detalles del grupo de desarrollo que contiene este desarrollo
      api.get(`v1/development-groups/${development.id}`).then((res) => {
        let group = res.data;
        let developments = group.properties.developments.features;
        let blocks = [];
        let arrayIndexStartAt = 0;

        // Itera a través de los desarrollos en el grupo
        developments.forEach((development) => {
          let maxArrayIndex = 0;
          // Obtén información sobre el desarrollo actual
          let developmentName = development.properties.name;

          development.properties.parcels.features.map((f) => {
            let divisor = 0;
            let lon = 0;
            let lat = 0;
            let lastValues = [0, 0];
            if (f.geometry) {
              for (let c of f.geometry.coordinates[0]) {
                lon = lon + c[0];
                lat = lat + c[1];
                lastValues = [c[0], c[1]];
                divisor = divisor + 1;
              }
              lon = (lon - lastValues[0]) / (divisor - 1);
              lat = (lat - lastValues[1]) / (divisor - 1);
              let arrayIndex = f.properties.block;
              if (parseInt(arrayIndex)) {
                arrayIndex = parseInt(arrayIndex);
              } else {
                arrayIndex = arrayIndex.toLowerCase().charCodeAt(0) - 96;
              }
              maxArrayIndex =
                maxArrayIndex > arrayIndex ? maxArrayIndex : arrayIndex;
              arrayIndex = arrayIndex + arrayIndexStartAt;
              if (blocks[arrayIndex]) {
                blocks[arrayIndex] = {
                  lon: blocks[arrayIndex].lon + parseFloat(lon),
                  lat: blocks[arrayIndex].lat + parseFloat(lat),
                  div: blocks[arrayIndex].div + 1,
                  name: f.properties.block,
                };
              } else {
                blocks[arrayIndex] = {
                  lon: parseFloat(lon),
                  lat: parseFloat(lat),
                  div: 1,
                  name: f.properties.block,
                };
              }
            }
            return (f.properties = {
              ...f.properties,
              centralPoint: f.geometry ? [lon, lat] : null,
              color: getColor(f.properties),
              developmentName: developmentName,
            });
          });
          arrayIndexStartAt = arrayIndexStartAt + maxArrayIndex + 1;
        });

        setSelectedLoteamiento(group);
        setBlocksCentralPoints(blocks);
        setParcels({
          type: "FeatureCollection",
          features: [].concat(...group.properties.developments.features.map((feature) => feature.properties.parcels.features) || [])
        })

        map.current.setState({
          currentParcels: {
            type: "FeatureCollection",
            features: selectedLoteamiento
              ? [].concat(
                ...selectedLoteamiento.properties.developments.features.map(
                  (f) => f.properties.parcels.features || []
                )
              )
              : [],
          },
          viewport: {
            ...map?.current?.state?.viewport || {},
          },
        });

        fly(development.geometry.coordinates[1], development.geometry.coordinates[0], true);
        setShowPortionsList(false);
      });
    }
  }

  const setLoteamiento = (development) => {
    api.get(`v1/development-groups/${development.id}`).then((res) => {
      let group = res.data;
      let developments = group.properties.developments.features; // Obtén la lista de desarrollos dentro del grupo
      let blocks = [];
      let arrayIndexStartAt = 0;

      // Itera a través de los desarrollos en el grupo
      developments.forEach((development) => {
        let maxArrayIndex = 0;
        // Obtén información sobre el desarrollo actual
        //let developmentName = development.properties.name;
        development.properties.parcels.features.map((f) => {
          let divisor = 0;
          let lon = 0;
          let lat = 0;
          let lastValues = [0, 0];
          if (f.geometry) {
            for (let c of f.geometry.coordinates[0]) {
              lon = lon + c[0];
              lat = lat + c[1];
              lastValues = [c[0], c[1]];
              divisor = divisor + 1;
            }
            lon = (lon - lastValues[0]) / (divisor - 1);
            lat = (lat - lastValues[1]) / (divisor - 1);
            let arrayIndex = f.properties.block;
            if (parseInt(arrayIndex)) {
              arrayIndex = parseInt(arrayIndex);
            } else {
              arrayIndex = arrayIndex.toLowerCase().charCodeAt(0) - 96;
            }
            maxArrayIndex =
              maxArrayIndex > arrayIndex ? maxArrayIndex : arrayIndex;
            arrayIndex = arrayIndex + arrayIndexStartAt;
            if (blocks[arrayIndex]) {
              blocks[arrayIndex] = {
                lon: blocks[arrayIndex].lon + parseFloat(lon),
                lat: blocks[arrayIndex].lat + parseFloat(lat),
                div: blocks[arrayIndex].div + 1,
                name: f.properties.block,
              };
            } else {
              blocks[arrayIndex] = {
                lon: parseFloat(lon),
                lat: parseFloat(lat),
                div: 1,
                name: f.properties.block,
              };
            }
          }
          return (f.properties = {
            ...f.properties,
            centralPoint: f.geometry ? [lon, lat] : null,
            color: getColor(f.properties),
          });
        });
        arrayIndexStartAt = arrayIndexStartAt + maxArrayIndex + 1;
      });

      // Actualiza el estado para mostrar el grupo y sus desarrollos
      setSelectedLoteamiento(group);
      setBlocksCentralPoints(blocks);
      setParcels({
        ...parcels,
        features: [].concat(...group.properties.developments.features.map((feature) => feature.properties.parcels.features) || [])
      })

      if (map?.current?.setState !== undefined) {
        map.current.setState({
          currentParcels: {
            type: "FeatureCollection",
            features: selectedLoteamiento
              ? [].concat(
                ...selectedLoteamiento.properties.developments.features.map(
                  (f) => f.properties.parcels.features || []
                )
              )
              : [],
          },
          viewport: {
            ...map?.current?.state?.viewport || {},
          },
        });

        fly(development.geometry.coordinates[1], development.geometry.coordinates[0], true);
      }

      setShowPortionsList(false);
    });
  }

  const setDistrito = (district) => {
    api.get(`v1/development-groups/?district=${district.id}`).then((res) => {
      if (res.data.features.length > 0) {
        setViewport({
          ...viewport,
          zoom: 8,
          latitude: res.data.features[0].geometry.coordinates[1],
          longitude: res.data.features[0].geometry.coordinates[0],
        });
      }
      unsetLoteamiento();
      setParcels(sortByName(res.data.features));

      map.current.setState({
        // markers: parcels,
        viewport: {
          ...map.current.state.viewport,
        },
      });

      fly(res.data.features[0].geometry.coordinates[1], res.data.features[0].geometry.coordinates[0], true, 11);
      setShowPortionsList(false);
    });
  }

  const unsetLoteamiento = () => {
    setSelectedLoteamiento(null);
    setBlocksCentralPoints([]);
    if (map.current && map.current.setState !== undefined) {
      map.current.setState({
        currentParcels: null,
      });
    }
  };

  const fly = (lat, lon, animation, zoom = 14.7) => {
    map.current.newLocationTransition(lat, lon, animation, zoom);
  }

  const togglePortionsList = () => {
    setShowPortionsList(!showPortionsList);
  }

  const applyFilters = async ({ searchText, maxPrice, districtId, departmentId }) => {
    setSearching(true);

    const params = {
      max_price: maxPrice,
    }

    if (districtId) {
      Object.assign(params, { district: districtId })
    }

    if (departmentId) {
      Object.assign(params, { district__department: departmentId })
    }

    const portionsResponse = await api.get(`v1/development-groups/`, { params });

    if (searchText !== undefined && searchText.length > 0) {
      portionsResponse.data.features = portionsResponse.data.features.filter(feature => feature.properties.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1);
    }

    setPortions(sortByName(portionsResponse.data.features))
    setDevelopments(sortByName(portionsResponse.data.features))
    setDevelopmentsSearched(sortByName(portionsResponse.data.features))
    setSearching(false);
  }

  const resetData = () => {
    setPortions(initData.portions)
    setDevelopments(initData.developments)
    setDevelopmentsSearched(initData.developmentsSearched)
    setDistricts(initData.districts)
    setDistrictsSearched(initData.districtsSearched)
    setDepartments(initData.departments);

    map.current = {
      markers: portions
    };
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <PortionsContext.Provider value={{ resetData, portions, developments, districts, map, developmentsSearched, districtsSearched, isLoading, search, currentSearch, clearSearch, setSelectedLoteamiento, setBlocksCentralPoints, selectedLoteamiento, blocksCentralPoints, setLoteamiento, setCurrentLoteamiento, unsetLoteamiento, parcels, setDistrito, viewport, lastUpdate, showPortionsList, togglePortionsList, departments, applyFilters, searching, fly }}>

      {children}
    </PortionsContext.Provider>
  )
}
